import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { LogoLockup } from "../components/brand/Brand";
import { Button } from "../components/ui/button";
import { Loader2, AlertTriangle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | timeout | error
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const { data: intake } = await api.post("/public/intake/start", { payment_session_id: sessionId });
          localStorage.setItem("lr_intake_token", intake.token);
          navigate(`/assessment/${intake.token}`, { replace: true });
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) { /* keep polling */ }
      if (attempts.current >= 24) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center paper-grain px-6" data-testid="payment-success-page">
      <LogoLockup className="h-16" />
      <div className="lr-card p-8 mt-8 max-w-md w-full text-center">
        {state === "checking" && (
          <>
            <Loader2 className="h-8 w-8 text-lr-olive animate-spin mx-auto" />
            <h1 className="font-serif text-2xl text-lr-charcoal mt-5">Confirming your payment</h1>
            <p className="mt-3 text-sm text-lr-charcoal/80" data-testid="payment-checking-msg">
              One moment. As soon as your Blueprint payment is confirmed, I will take you straight into your Business Assessment.
            </p>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <AlertTriangle className="h-8 w-8 text-lr-clay mx-auto" />
            <h1 className="font-serif text-2xl text-lr-charcoal mt-5">
              {state === "timeout" ? "Still confirming" : "We could not confirm this payment"}
            </h1>
            <p className="mt-3 text-sm text-lr-charcoal/80" data-testid="payment-error-msg">
              {state === "timeout"
                ? "Your payment is taking a little longer than usual to confirm. You can wait a moment and refresh, or reach out and I will sort it out with you."
                : "If you were charged, do not worry. Reach out and I will get you started right away."}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              {state === "timeout" && (
                <Button onClick={() => window.location.reload()} data-testid="payment-refresh-btn"
                  className="bg-lr-olive hover:bg-[#3d4230] text-lr-cream rounded-[4px]">Refresh</Button>
              )}
              <Button variant="outline" onClick={() => navigate("/")} data-testid="payment-home-btn"
                className="rounded-[4px] border-lr-olive/40 text-lr-olive hover:bg-lr-olive/10">Back to home</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
